import HttpMixin from "@/mixins/HttpMixin";
import distanceInWords from "date-fns/distance_in_words";
import {ConversationSummary, UiMessage} from "@/components/communications/CommunicationInterfaces";
import {
  ActionPayload,
  MESSAGE_ACTIONS,
  MessageAction,
  ReadActionPayload
} from "@/components/communications/cards/actions/Actions";
import {BallPulseLoader} from "vue-loaders";
import CheckboxControl from "@/componentsReusable/CheckboxControl.vue";
import {Container, Draggable} from "vue-smooth-dnd";
import MessageItem from "@/components/communications/cards/MessageItem";
import VueSimpleSpinner from "vue-simple-spinner";
import ActionsDropdown from "@/componentsReusable/dropdowns/ActionsDropdown.vue";

/**
 * Mixin to bring in common Message Column Functionality.
 */
export default {
  components: {
    ActionsDropdown,
    BallPulseLoader,
    CheckboxControl,
    Container,
    Draggable,
    MessageItem,
    VueSimpleSpinner
  },
  filters: {
    timeAgo(incomingDate: Date, currentDate: Date){
      return distanceInWords(
        incomingDate,
        currentDate
      )
    }
  },
  mixins: [ HttpMixin ],
  data() {
    return {
      actions: MESSAGE_ACTIONS,
      currentDate: new Date(),
      intervalCurrentDate: null as any,
      mayReorder: false,
      messages: [] as UiMessage[],
      moving: false,
      showActions: false,
      // Track separately since this is not stored in Vuex. Vuex is storing only the data from the server.
      expandedMessageIds: []
    };
  },
  async created () {
    (this as any).intervalCurrentDate = setInterval(() => (this as any).currentDate = new Date(), 1000 * 60);
  },
  beforeDestroy () {
    clearInterval((this as any).intervalCurrentDate);
  },
  methods: {
    setMessages(data: ConversationSummary[]) {
      // Because a message gets marked as read when it's expanded, the data gets refreshed so we need to maintain what should be expanded.
      const expandedIds = new Set<string>((this as any).messages.filter((m: UiMessage) => m.isExpanded).map((message: UiMessage) => message.id));
      (this as any).messages = data.map(message => this.convertToUiMessage(message, expandedIds));
    },
    convertToUiMessage(message: ConversationSummary, expandedIds: Set<string>): UiMessage {
      return {
        id: message.id,
        isRead: message.isRead,
        isStarred: false,
        isChecked: false,
        targetFirstName: message.contactFirstName,
        targetLastName: message.contactLastName,
        lastActiveTime: message.occurredOn,
        latestCampaign: message.campaignTitle || '',
        messagePreview: message.messageContent.substring(0, 100),
        isExpanded: expandedIds.has(message.id),
        providerId: message.providerId,
        facilityContactId: message.facilityContactId,
        deliveryStatus: message.deliveryStatus,
        providerTitle: message.providerTitle
      };
    },
    async toggleMessageExpanded (message: UiMessage) {
      if ((this as any).moving) {
        return;
      }
      message.isExpanded = !message.isExpanded;
      if (message.isExpanded) {
        (this as any).expandedMessageIds.push(message.id);
        await (this as any).messageRead({ messageIds: [message.id], isRead: true });
      } else {
        const index = (this as any).expandedMessageIds.findIndex((messageId: string) => messageId === message.id);
        if (index > -1) {
          (this as any).expandedMessageIds.splice(index, 1);
        }
      }
    },
    async messageRead(payload: ReadActionPayload) {
      await (this as any).$store.dispatch('communication/messageRead', payload);
    },
    async messageClosed(payload: ActionPayload) {
      await (this as any).$store.dispatch('communication/messageClosed', payload);
    },
    async messageOptedOut(payload: ActionPayload) {
      await (this as any).$store.dispatch('communication/messageOptedOut', payload);
    },
    closeActions() {
      (this as any).showActions = false;
    },
    async actionSelected(actionId: number) {
      this.closeActions();
      const actionPayload = { messageIds: (this as any).messages
          .filter((message: UiMessage) => message.isChecked)
          .map((message: UiMessage) => message.id) };
      switch (actionId) {
        case MessageAction.MarkUnread:
          (actionPayload as ReadActionPayload).isRead = false;
          await this.messageRead(actionPayload as ReadActionPayload);
          break;
        case MessageAction.MarkRead:
          (actionPayload as ReadActionPayload).isRead = true;
          await this.messageRead(actionPayload as ReadActionPayload);
          break;
        case MessageAction.Close:
          await this.messageClosed(actionPayload);
          break;
        case MessageAction.OptOut:
          await this.messageOptedOut(actionPayload);
          break;
      }
    },
    getChildPayload (index: number): UiMessage {
      (this as any).moving = true;
      return (this as any).messages[index];
    },
    canDrag(message: UiMessage) {
      return !message.isExpanded;
    },
    canReorder(source: any, payload: UiMessage) {
      // If the column may reorder, it can also accept drops.
      // If we're moving the message within the column (reorder) that's allowed,
      // but if the payload is for the same message and they're dropping it from somewhere else, that's not allowed (to prevent duplicates)
      if ((this as any).mayReorder) {
        const containsMessage = (this as any).messages.find((message: UiMessage) => message.id === payload.id);
        return !containsMessage || (this as any).moving === true;
      }
      return false;
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/communications/cards/mixins/MessageColumnMixin.ts